import type { Dispatch, SetStateAction } from "react";
import styled from "styled-components/macro";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import {
  DeleteButton,
  PrimaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import { PlusIcon } from "../../../../../components/Icons/Icons";
import { AccordionCard } from "../../../../../components/AccordionCard/AccordionCard";
import {
  TextBaseSemibold,
  TextSmRegular,
} from "../../../../../components/Typography/TypographyV2";
import {
  FormSectionCard,
  FormSectionHeading,
} from "../../../../../components/Form/Form";
import type { MethodsOfUseForm, User } from "../../../../../types/types";
import { PointOfContact } from "./PointOfContactForm";
import type { LocationFormPOCInput } from "./LocationForm";

const OuterCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
`;

const TruncatedTitle = styled(TextBaseSemibold)`
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const TruncatedSupportingText = styled(TextSmRegular)`
  display: block;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const pocRowTitle = (
  poc: LocationFormPOCInput | undefined,
  t: (s: string) => string
) => {
  if (!poc) return t("New Point of Contact");
  if (poc.poc_type === "existing") {
    const option = poc.point_of_contact_id;
    return option?.primaryText || option?.label || t("New Point of Contact");
  }
  const name = `${poc.contact_first_name ?? ""} ${
    poc.contact_last_name ?? ""
  }`.trim();
  return name || t("New Point of Contact");
};

interface PointOfContactAccordionProps {
  methodsOfUseForm: MethodsOfUseForm;
  fields: Partial<LocationFormPOCInput>[];
  append: (poc: LocationFormPOCInput) => void;
  remove: (index?: number | number[]) => void;
  defaultExistingUser: User;
  disabled?: boolean;
}

/**
 * The collapsible list of Points of Contact on a shipping location's
 * add/edit form. Renders one collapsible card per entry (reusing the
 * existing/manual `PointOfContact` sub-form per row), a trailing delete
 * icon that hides once only one entry remains, and a trailing "+ Add"
 * button.
 */
export const PointOfContactAccordion = ({
  methodsOfUseForm,
  fields,
  append,
  remove,
  defaultExistingUser,
  disabled = false,
}: PointOfContactAccordionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { errors, setValue, watch } = methodsOfUseForm;
  const rowValues: LocationFormPOCInput[] | undefined =
    watch("points_of_contact");

  return (
    <OuterCard>
      <FormSectionHeading>{t("Point of Contact")}</FormSectionHeading>
      {fields.map((field, index) => {
        const rowValue = rowValues?.[index] ?? (field as LocationFormPOCInput);
        const setRowPointOfContactType: Dispatch<
          SetStateAction<"manual" | "existing">
        > = (action) => {
          const current = rowValue?.poc_type ?? "manual";
          const next =
            typeof action === "function"
              ? (
                  action as (
                    prev: "manual" | "existing"
                  ) => "manual" | "existing"
                )(current)
              : action;
          setValue(`points_of_contact[${index}].poc_type`, next);
        };
        const excludeUserIds = (rowValues ?? [])
          .filter((_, otherIndex) => otherIndex !== index)
          .map((poc) => poc.point_of_contact_id?.value)
          .filter((value): value is string => Boolean(value));

        const supportingText =
          rowValue?.poc_type === "existing"
            ? rowValue.point_of_contact_id?.supportingText
            : undefined;

        return (
          <FormSectionCard key={field.id ?? index}>
            <AccordionCard
              title={() => (
                <>
                  <TruncatedTitle>{pocRowTitle(rowValue, t)}</TruncatedTitle>
                  {supportingText && (
                    <TruncatedSupportingText
                      color="secondary"
                      title={supportingText}
                    >
                      {supportingText}
                    </TruncatedSupportingText>
                  )}
                </>
              )}
              expanded={fields.length === 1}
              hideHeaderBorder
              actionChildren={() =>
                fields.length > 1 ? (
                  <DeleteButton
                    testid={`poc-delete-button-${index}`}
                    type="button"
                    height={20}
                    width={20}
                    disabled={disabled}
                    onClick={() => remove(index)}
                  />
                ) : (
                  <></>
                )
              }
            >
              <PointOfContact
                methodsOfUseForm={methodsOfUseForm}
                pointOfContactType={rowValue?.poc_type ?? "manual"}
                setPointOfContactType={setRowPointOfContactType}
                defaultExistingUser={defaultExistingUser}
                fieldNamePrefix={`points_of_contact[${index}]`}
                rowErrors={errors?.points_of_contact?.[index]}
                excludeUserIds={excludeUserIds}
                hideHeading
                disabled={disabled}
              />
            </AccordionCard>
          </FormSectionCard>
        );
      })}
      <PrimaryButtonMediumV2
        testid="poc-add-button"
        type="button"
        disabled={disabled}
        style={{
          marginTop: theme.resolve("spacing_lg"),
          alignSelf: "flex-start",
        }}
        onClick={() => append({ poc_type: "manual" })}
      >
        <PlusIcon />
        {t("Add")}
      </PrimaryButtonMediumV2>
    </OuterCard>
  );
};
